import { PromptRun } from '../services/api';
import { formatCredits } from './credits';

export type PromptRunBillingTone = 'success' | 'warning' | 'danger' | 'muted';

export interface PromptRunBillingDisplayState {
  visible: boolean;
  tone: PromptRunBillingTone;
  label: string;
  detail: string;
}

const formatBillingCredits = (value?: number | null): string => {
  const amount = Number(value || 0);
  if (!Number.isFinite(amount) || amount <= 0) {
    return '0';
  }
  return formatCredits(amount, {
    minimumFractionDigits: amount < 1 ? 4 : 2,
    maximumFractionDigits: amount < 1 ? 4 : 2,
  });
};

export const getPromptRunBillingDisplayState = (
  run: Pick<PromptRun, 'billing_status' | 'billing_error' | 'billing_actual_mt' | 'billing_estimated_mt'>
): PromptRunBillingDisplayState => {
  const normalizedStatus = String(run.billing_status || '').trim().toLowerCase();
  const error = String(run.billing_error || '').trim();

  if (error || normalizedStatus === 'pending_confirmation' || normalizedStatus === 'pending_release') {
    return {
      visible: true,
      tone: error ? 'danger' : 'warning',
      label: error ? 'Billing action needed' : 'Billing pending',
      detail: error || `Reservation settlement is ${normalizedStatus.replace('_', ' ')}.`,
    };
  }

  if (normalizedStatus === 'pending') {
    return {
      visible: true,
      tone: 'warning',
      label: 'Billing reserved',
      detail: `Estimated hold ${formatBillingCredits(run.billing_estimated_mt)} credits.`,
    };
  }

  if (normalizedStatus === 'confirmed') {
    return {
      visible: true,
      tone: 'success',
      label: `Billed ${formatBillingCredits(run.billing_actual_mt)}`,
      detail: 'Reservation confirmed for actual persisted usage.',
    };
  }

  if (normalizedStatus === 'released' || normalizedStatus === 'expired') {
    return {
      visible: true,
      tone: 'muted',
      label: normalizedStatus === 'released' ? 'Billing released' : 'Billing expired',
      detail: 'No final usage was charged for this reservation.',
    };
  }

  return {
    visible: false,
    tone: 'muted',
    label: 'Billing unavailable',
    detail: '',
  };
};
